/**
 * SwiperJS Infinite Scroll Coordinator for Sunice Slideshows
 * Coordinates initialization of different swiper types using separate modules
 */

// SwiperJS is loaded via CDN, so it's available globally

class SuniceSwiperInfinite {
  constructor() {
    this.swiperModules = new Map();
    this.init();
  }

  init() {
    // Wait for DOM to be ready and all swiper modules to be loaded
    const initializeWhenReady = () => {
      // Check if all required modules are available
      const modulesReady = typeof Swiper !== 'undefined' && 
                          (typeof window.SuniceProductGridSwiper !== 'undefined' || 
                           typeof window.SuniceColorSwiper !== 'undefined');
      
      if (modulesReady || document.readyState === 'complete') {
        // Small delay to ensure all sections are rendered
        setTimeout(() => this.setupSwiperSlideshows(), 100);
      } else {
        // Retry after a short delay
        setTimeout(initializeWhenReady, 100);
      }
    };
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeWhenReady);
    } else {
      initializeWhenReady();
    }
  }

  setupSwiperSlideshows() {
    // Initialize swiper modules
    this.initializeSwiperModules();
    
    // Initialize main product grid swipers first
    this.initializeMainSwipers();
    
    // Then initialize color swatch swipers
    this.initializeColorSwatchSwipers();
  }

  initializeSwiperModules() {
    // Initialize each swiper module
    if (window.SuniceProductGridSwiper) {
      this.swiperModules.set('productGrid', new window.SuniceProductGridSwiper());
    }
    if (window.SuniceTargetedProductsSwiper) {
      this.swiperModules.set('targetedProducts', new window.SuniceTargetedProductsSwiper());
    }
    if (window.SuniceToggleCollectionSwiper) {
      this.swiperModules.set('toggleCollection', new window.SuniceToggleCollectionSwiper());
    }
    // Use the existing singleton instance if available, otherwise create new one
    if (window.SuniceColorSwiperInstance) {
      // Use the existing singleton instance that auto-initialization created
      this.swiperModules.set('colorSwiper', window.SuniceColorSwiperInstance);
      console.log('Using existing SuniceColorSwiperInstance singleton');
    } else if (window.SuniceColorSwiper) {
      // Fallback: create new instance if singleton doesn't exist yet
      this.swiperModules.set('colorSwiper', new window.SuniceColorSwiper());
      console.log('Created new SuniceColorSwiper instance');
    } else {
      console.warn('SuniceColorSwiper not available yet, will retry');
      // Retry after a short delay if module isn't loaded yet
      setTimeout(() => {
        if (window.SuniceColorSwiperInstance) {
          this.swiperModules.set('colorSwiper', window.SuniceColorSwiperInstance);
          console.log('Retry: Using existing SuniceColorSwiperInstance singleton');
        } else if (window.SuniceColorSwiper && !this.swiperModules.has('colorSwiper')) {
          this.swiperModules.set('colorSwiper', new window.SuniceColorSwiper());
          console.log('Retry: Created new SuniceColorSwiper instance');
        }
        // Don't call initializeColorSwatchSwipers here - the auto-init handles it
      }, 200);
    }
  }

  initializeMainSwipers(retryCount = 0) {
    const MAX_RETRIES = 10; // Maximum number of retries (2 seconds total)
    
    // Initialize product grid swipers - use unique IDs to ensure each section is targeted
    const productGridContainers = document.querySelectorAll('[id^="product-grid-swiper-"]');
    
    // Fallback to class selector if ID selector doesn't find anything
    let containers = productGridContainers;
    if (containers.length === 0) {
      containers = document.querySelectorAll('.sunice-product-grid-swiper');
    }
    
    if (containers.length === 0) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => this.initializeMainSwipers(retryCount + 1), 200);
      } else {
        console.warn('No product grid containers found after', MAX_RETRIES, 'retries. Skipping initialization.');
      }
      return;
    }
    
    console.log('Found product grid containers:', containers.length);
    
    containers.forEach((container, index) => {
      const productGridModule = this.swiperModules.get('productGrid');
      if (productGridModule) {
        // Ensure container has a unique ID
        if (!container.id) {
          console.warn(`Container ${index} has no ID, generating one...`);
          container.id = `product-grid-swiper-${Date.now()}-${index}`;
        }
        
        const slideCount = container.querySelectorAll('.swiper-slide').length;
        console.log(`Initializing product grid swiper ${index}:`, container.id, `(${slideCount} slides)`);
        
        if (slideCount === 0) {
          console.warn(`Container ${container.id} has no slides, skipping initialization`);
          return;
        }
        
        productGridModule.initializeProductGridSwiper(container);
      } else {
        console.error('Product grid module not found');
      }
    });

    // Initialize targeted products swipers
    const targetedProductsContainers = document.querySelectorAll('.sunice-targeted-products-swiper');
    targetedProductsContainers.forEach((container, index) => {
      const targetedProductsModule = this.swiperModules.get('targetedProducts');
      if (targetedProductsModule) {
        targetedProductsModule.initializeTargetedProductsSwiper(container);
      }
    });

    // Initialize toggle collection list swipers
    const toggleCollectionContainers = document.querySelectorAll('.sunice-toggle-collection-swiper');
    toggleCollectionContainers.forEach((container, index) => {
      const toggleCollectionModule = this.swiperModules.get('toggleCollection');
      if (toggleCollectionModule) {
        toggleCollectionModule.initializeToggleCollectionSwiper(container);
      }
    });
  }

  initializeColorSwatchSwipers() {
    // Color swipers are auto-initialized by sunice-color-swiper.js
    // We don't need to manually initialize them here, but we can check if they exist
    const colorSwiperContainers = document.querySelectorAll('.color-swiper-container');
    console.log('Found color swiper containers:', colorSwiperContainers.length);
    
    // The color swiper has its own auto-initialization that handles this
    // We just need to make sure the module is available
    if (window.SuniceColorSwiperInstance) {
      console.log('Color swiper auto-initialization is active');
    } else if (window.SuniceColorSwiper) {
      console.log('Color swiper class available, but singleton not created yet');
    } else {
      console.warn('Color swiper not available');
    }
  }

  destroyAllSwipers() {
    // Destroy all swipers in each module
    this.swiperModules.forEach((module, moduleName) => {
      if (module && typeof module.destroyProductGridSwipers === 'function') {
        module.destroyProductGridSwipers();
      }
      if (module && typeof module.destroyTargetedProductsSwipers === 'function') {
        module.destroyTargetedProductsSwipers();
      }
      if (module && typeof module.destroyToggleCollectionSwipers === 'function') {
        module.destroyToggleCollectionSwipers();
      }
      if (module && typeof module.destroyColorSwipers === 'function') {
        module.destroyColorSwipers();
      }
    });
    this.swiperModules.clear();
  }
}

// Initialize SwiperJS infinite scroll
// Wait for SuniceProductGridSwiper to be available if script is deferred
function initializeSuniceSwiperInfinite() {
  if (typeof window.SuniceProductGridSwiper === 'undefined') {
    // Wait a bit more for deferred scripts
    setTimeout(initializeSuniceSwiperInfinite, 50);
    return;
  }
  
  const suniceSwiperInfinite = new SuniceSwiperInfinite();
  
  // Expose globally so sections can access it
  window.suniceSwiperInfinite = suniceSwiperInfinite;
}

// Start initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSuniceSwiperInfinite);
} else {
  initializeSuniceSwiperInfinite();
}

// Re-initialize when theme editor updates sections
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    if (window.suniceSwiperInfinite) {
      window.suniceSwiperInfinite.destroyAllSwipers();
      // Re-initialize modules and swipers
      window.suniceSwiperInfinite.initializeSwiperModules();
      window.suniceSwiperInfinite.initializeMainSwipers();
      window.suniceSwiperInfinite.initializeColorSwatchSwipers();
    }
  }, 200);
});

document.addEventListener('shopify:section:reorder', () => {
  setTimeout(() => {
    suniceSwiperInfinite.destroyAllSwipers();
    // Re-initialize modules and swipers
    suniceSwiperInfinite.initializeSwiperModules();
    suniceSwiperInfinite.initializeMainSwipers();
    suniceSwiperInfinite.initializeColorSwatchSwipers();
  }, 100);
});

// Re-initialize swipers when lazy-loaded sections become visible
document.addEventListener('section:lazy-load', (event) => {
  const section = event.target;
  if (!section) return;
  
  // Wait for section to be fully visible and DOM ready
  setTimeout(() => {
    if (window.suniceSwiperInfinite) {
      // Re-initialize product grid swipers in this section
      const productGridSwipers = section.querySelectorAll('.sunice-product-grid-swiper, [id^="product-grid-swiper-"]');
      if (productGridSwipers.length > 0) {
        const productGridModule = window.suniceSwiperInfinite.swiperModules.get('productGrid');
        if (productGridModule) {
          productGridSwipers.forEach(container => {
            // Reset initialization flag to allow re-initialization
            if (container.dataset.initStarted) {
              delete container.dataset.initStarted;
            }
            // Wait for images to load before initializing to prevent massive gap
            const images = container.querySelectorAll('img');
            let imagesLoaded = 0;
            const totalImages = images.length;
            
            const initSwiper = () => {
              productGridModule.initializeProductGridSwiper(container);
            };
            
            if (totalImages === 0) {
              initSwiper();
            } else {
              images.forEach(img => {
                if (img.complete) {
                  imagesLoaded++;
                } else {
                  img.addEventListener('load', () => {
                    imagesLoaded++;
                    if (imagesLoaded === totalImages) {
                      initSwiper();
                    }
                  }, { once: true });
                  img.addEventListener('error', () => {
                    imagesLoaded++;
                    if (imagesLoaded === totalImages) {
                      initSwiper();
                    }
                  }, { once: true });
                }
              });
              
              if (imagesLoaded === totalImages) {
                initSwiper();
              }
              
              // Fallback timeout
              setTimeout(initSwiper, 1000);
            }
          });
        }
      }
      
      // Re-initialize color swipers in the section
      if (window.SuniceColorSwiperInstance) {
        const colorSwiperContainers = section.querySelectorAll('.color-swiper-container:not([data-sunice-color-swiper-initialized])');
        colorSwiperContainers.forEach(container => {
          window.SuniceColorSwiperInstance.initializeColorSwiper(container);
        });
      }
    }
  }, 200);
});
